import { GETHospitalWardList, GETUserHospitalWardList } from "api/user";
import { IWard } from "api/user/types";
import { SelectInput } from "components/select/select";
import { ISelectProps } from "components/select/types";
import { useEffect, useMemo, useState, type FC } from "react";
import { useDispatch, useSelector } from "react-redux";
import { changeWardList } from "state-manager/reducer/hospital";
import { RootState } from "state-manager/store";

let wardListPromise: Promise<IWard[]> | null = null;

interface IWardSelectProps
  extends Omit<ISelectProps, "options" | "isLoading" | "value" | "onChange"> {
  wardId: string | null | undefined;
  onChange: (id: string | null, label?: string | null) => void;
  hospitalId?: string;
}

export const WardSelect: FC<IWardSelectProps> = ({ ...props }) => {
  const { onChange, wardId, hospitalId } = props;
  const [isLoading, setIsLoading] = useState(false);
  const dispatch = useDispatch();
  const { wardList } = useSelector((state: RootState) => state.hospital);

  useEffect(() => {
    const fetchWardList = async () => {
      if (wardList.length === 0 && !wardListPromise) {
        setIsLoading(true);

        wardListPromise = hospitalId
          ? GETHospitalWardList({ hospitalId })
          : GETUserHospitalWardList();

        wardListPromise
          .then((res) => {
            dispatch(
              changeWardList(res.filter(({ name }) => name !== "بخش ناشناس"))
            );
            setIsLoading(false);
          })
          .finally(() => {
            wardListPromise = null;
          });
      } else if (wardListPromise) {
        setIsLoading(true);
        wardListPromise.finally(() => setIsLoading(false));
      }
    };

    fetchWardList();
  }, [hospitalId, wardList, dispatch]);

  const wardOptions: IGSelectOption[] = useMemo(() => {
    return wardList.map((ward) => ({
      label: ward.name,
      value: `${ward.id}`,
    }));
  }, [wardList]);

  const wardValue = () => {
    const option = wardOptions.find((option) => option.value === wardId);
    if (option) {
      return {
        label: option.label,
        value: option.value,
      };
    } else {
      return null;
    }
  };

  const onChangeWard = (ward: IGSelectOption) => {
    onChange(ward?.value || null, ward?.label || null);
  };

  return (
    <SelectInput
      {...props}
      isLoading={isLoading}
      options={wardOptions}
      value={wardValue()}
      onChange={onChangeWard}
    />
  );
};
